import { useState } from "react";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EditNoteModal } from "@/components/notes/EditNoteModal";
import { showError } from "@/lib/utils/notifications";
import { formatLargeFileWarningList } from "../../utils/notesTabHelpers";
import { ApplicationMailConversationModal } from "../modals/ApplicationMailConversationModal";
import { EmailBroadcastModal } from "../modals/EmailBroadcastModal";
import type {
  ApplicationDetailData,
  ApplicationLayerData,
} from "../../types/application";
import type { NotesTabModalsProps } from "../../types";

export function NotesTabModals({ tab }: NotesTabModalsProps) {
  const {
    mailConversationOpen,
    setMailConversationOpen,
    mailLogs,
    selectedMailId,
    applicationAllData,
    applicationData,
    applicantId,
    largeFileConfirm,
    removeAdditionalFileConfirm,
    setRemoveAdditionalFileConfirm,
    removeNoteAttachmentConfirm,
    setRemoveNoteAttachmentConfirm,
    chargebackOverrideConfirm,
    setChargebackOverrideConfirm,
    defaultOverrideConfirm,
    setDefaultOverrideConfirm,
    performRemoveAdditionalFile,
    performRemoveNoteAttachment,
    performChargebackExecutiveOverride,
    performDefaultExecutiveOverride,
    handleLargeFileConfirmPrimary,
    handleLargeFileConfirmSecondary,
    editingNote,
    setEditingNote,
    getNoteRowId,
    handleNoteSaved,
  } = tab;

  const [replyBroadcast, setReplyBroadcast] = useState<{
    open: boolean;
    subject: string;
  }>({ open: false, subject: "" });

  const applicantEmail =
    typeof applicationData?.user_data?.email === "string"
      ? applicationData.user_data.email
      : "";

  const handleReplyToMail = (mail: { subject: string }) => {
    // Angular EmailParserDetail.replyToMail
    if (!applicantEmail) {
      showError("There is no Email for Applicant");
      return;
    }
    setReplyBroadcast({ open: true, subject: mail.subject || "" });
  };

  return (
    <>
      <ApplicationMailConversationModal
        isOpen={mailConversationOpen}
        onClose={() => setMailConversationOpen(false)}
        mailLogs={mailLogs}
        scrollToMailId={selectedMailId}
        applicantEmail={applicantEmail}
        onReplyToMail={handleReplyToMail}
      />

      {replyBroadcast.open && applicationAllData && applicationData ? (
        <EmailBroadcastModal
          applicantId={String(applicantId ?? "")}
          applicationAllData={applicationAllData as ApplicationDetailData}
          applicationData={applicationData as ApplicationLayerData}
          initialSubject={replyBroadcast.subject}
          onClose={() => setReplyBroadcast({ open: false, subject: "" })}
          onSuccess={() => setReplyBroadcast({ open: false, subject: "" })}
        />
      ) : null}

      <ConfirmDialog
        isOpen={largeFileConfirm.open}
        title="Continue Upload"
        message={
          "Please confirm you want to continue uploading large file(s):\n" +
          formatLargeFileWarningList(largeFileConfirm.warnings)
        }
        primaryLabel="Yes, upload"
        onPrimary={handleLargeFileConfirmPrimary}
        secondaryLabel="Cancel"
        onSecondary={handleLargeFileConfirmSecondary}
        variant="warning"
        overlayDataTestId="application-notes-tab-confirm-large-file-upload"
        primaryButtonDataTestId="application-notes-tab-confirm-large-file-upload-primary-button"
        secondaryButtonDataTestId="application-notes-tab-confirm-large-file-upload-secondary-button"
        closeButtonDataTestId="application-notes-tab-confirm-large-file-upload-close-button"
      />

      <ConfirmDialog
        isOpen={removeAdditionalFileConfirm.open}
        title="Remove File"
        message="Are you sure you want to remove this Additional file?"
        primaryLabel="Remove"
        onPrimary={() => void performRemoveAdditionalFile()}
        secondaryLabel="Cancel"
        onSecondary={() =>
          setRemoveAdditionalFileConfirm({ open: false, attachmentId: null })
        }
        variant="warning"
        overlayDataTestId="application-notes-tab-confirm-remove-additional-file"
        primaryButtonDataTestId="application-notes-tab-confirm-remove-additional-file-primary-button"
        secondaryButtonDataTestId="application-notes-tab-confirm-remove-additional-file-secondary-button"
        closeButtonDataTestId="application-notes-tab-confirm-remove-additional-file-close-button"
      />

      <ConfirmDialog
        isOpen={removeNoteAttachmentConfirm.open}
        title="Remove Attachment"
        message="Please confirm that you really want to remove this file."
        primaryLabel="Remove"
        onPrimary={() => void performRemoveNoteAttachment()}
        secondaryLabel="Cancel"
        onSecondary={() =>
          setRemoveNoteAttachmentConfirm({ open: false, fileId: null })
        }
        variant="warning"
        overlayDataTestId="application-notes-tab-confirm-remove-note-attachment"
        primaryButtonDataTestId="application-notes-tab-confirm-remove-note-attachment-primary-button"
        secondaryButtonDataTestId="application-notes-tab-confirm-remove-note-attachment-secondary-button"
        closeButtonDataTestId="application-notes-tab-confirm-remove-note-attachment-close-button"
      />

      <ConfirmDialog
        isOpen={chargebackOverrideConfirm.open}
        title="Confirm"
        message={
          chargebackOverrideConfirm.value === "1"
            ? "Change Previous Chargeback Executive Override to Yes"
            : "Change Previous Chargeback Executive Override to No"
        }
        primaryLabel="Confirm"
        onPrimary={() => void performChargebackExecutiveOverride()}
        secondaryLabel="Cancel"
        primaryFirst
        onSecondary={() =>
          setChargebackOverrideConfirm({ open: false, value: null })
        }
        variant="warning"
        overlayDataTestId="application-notes-tab-confirm-chargeback-override"
        primaryButtonDataTestId="application-notes-tab-confirm-chargeback-override-primary-button"
        secondaryButtonDataTestId="application-notes-tab-confirm-chargeback-override-secondary-button"
        closeButtonDataTestId="application-notes-tab-confirm-chargeback-override-close-button"
      />

      <ConfirmDialog
        isOpen={defaultOverrideConfirm.open}
        title="Confirm"
        message={
          defaultOverrideConfirm.value === "1"
            ? "Has this applicant repaid their previous default?"
            : "Change Previous Default Executive Override to No"
        }
        primaryLabel="Confirm"
        onPrimary={() => void performDefaultExecutiveOverride()}
        secondaryLabel="Cancel"
        primaryFirst
        onSecondary={() =>
          setDefaultOverrideConfirm({ open: false, value: null })
        }
        variant="warning"
        overlayDataTestId="application-notes-tab-confirm-default-override"
        primaryButtonDataTestId="application-notes-tab-confirm-default-override-primary-button"
        secondaryButtonDataTestId="application-notes-tab-confirm-default-override-secondary-button"
        closeButtonDataTestId="application-notes-tab-confirm-default-override-close-button"
      />

      <EditNoteModal
        isOpen={!!editingNote}
        notesId={editingNote ? getNoteRowId(editingNote) : null}
        initialNotes={
          editingNote?.notes ??
          editingNote?.note ??
          editingNote?.note_text ??
          ""
        }
        onClose={() => setEditingNote(null)}
        onSaved={handleNoteSaved}
        testIdPrefix="application-notes-tab-edit-note"
      />
    </>
  );
}
